"use client"

import { useState, createContext, useContext } from "react"
import React from "react"

// Create context for tabs
const TabsContext = createContext({
  selectedTab: "",
  setSelectedTab: () => {},
})

const Tabs = ({ defaultValue, value, onValueChange, className = "", ...props }) => {
  const [uncontrolledSelectedTab, setUncontrolledSelectedTab] = useState(defaultValue || value || "")
  const isControlled = value !== undefined
  const selectedTab = isControlled ? value : uncontrolledSelectedTab

  const setSelectedTab = (nextValue) => {
    if (!isControlled) {
      setUncontrolledSelectedTab(nextValue)
    }

    onValueChange?.(nextValue)
  }

  return (
    <TabsContext.Provider value={{ selectedTab, setSelectedTab }}>
      <div className={`w-full ${className}`} {...props} />
    </TabsContext.Provider>
  )
}

const TabsList = React.forwardRef(({ className = "", ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="tablist"
      className={`inline-flex items-center justify-center rounded-md bg-gray-100 p-1 text-gray-500 ${className}`}
      {...props}
    />
  )
})

TabsList.displayName = "TabsList"

const TabsTrigger = ({ value, className = "", ...props }) => {
  const { selectedTab, setSelectedTab } = useContext(TabsContext)
  const isSelected = selectedTab === value

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isSelected}
      data-state={isSelected ? "active" : "inactive"}
      onClick={() => setSelectedTab(value)}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
        isSelected ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
      } ${className}`}
      {...props}
    />
  )
}

const TabsContent = React.forwardRef(({ className = "", value, children, ...props }, ref) => {
  const { selectedTab } = useContext(TabsContext)
  const isSelected = selectedTab === value

  // Always render the content but control visibility with CSS
  return (
    <div
      ref={ref}
      role="tabpanel"
      data-state={isSelected ? "active" : "inactive"}
      className={`mt-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ${
        isSelected ? "block" : "hidden"
      } ${className}`}
      {...props}
    >
      {children}
    </div>
  )
})

TabsContent.displayName = "TabsContent"

export { Tabs, TabsList, TabsTrigger, TabsContent }
